import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { LOCALES, type Locale } from '@motorcity/shared';

interface EditLocaleValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
}

const EditLocaleContext = createContext<EditLocaleValue | undefined>(undefined);

/**
 * One language switch for the whole editor rather than a tab strip on every
 * field.
 *
 * A model form has seven translatable fields plus a USP list; per-field tabs
 * meant seven separate controls to flip, and no way to read the Arabic version
 * of a page as a whole. Lifting it here means an editor sets the language once
 * and the entire form follows.
 */
export function EditLocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>('en');
  const value = useMemo(() => ({ locale, setLocale }), [locale]);
  return <EditLocaleContext.Provider value={value}>{children}</EditLocaleContext.Provider>;
}

export function useEditLocale(): EditLocaleValue {
  const ctx = useContext(EditLocaleContext);
  if (!ctx) throw new Error('useEditLocale must be used inside <EditLocaleProvider>');
  return ctx;
}

const LABELS: Record<Locale, string> = { en: 'English', ar: 'العربية' };

/**
 * Segmented control for the active editing language. `missing` counts fields
 * with no translation yet, so the gap is visible before publishing rather than
 * discovered in the app.
 */
export function LocaleSwitch({ missing }: { missing?: Record<Locale, number> }) {
  const { locale, setLocale } = useEditLocale();
  return (
    <div className="segmented" role="tablist" aria-label="Editing language">
      {LOCALES.map((l) => {
        const gap = missing?.[l] ?? 0;
        return (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={locale === l}
            className={locale === l ? 'segment active' : 'segment'}
            onClick={() => setLocale(l)}
          >
            <span lang={l}>{LABELS[l]}</span>
            {gap > 0 ? (
              <span className="segment-count" title={`${gap} field${gap === 1 ? '' : 's'} untranslated`}>
                {gap}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Count translatable fields that are still blank for a given locale. */
export function countMissing(
  values: Array<{ en: string; ar: string } | undefined>,
): Record<Locale, number> {
  const missing: Record<Locale, number> = { en: 0, ar: 0 };
  for (const value of values) {
    if (!value) continue;
    for (const locale of LOCALES) {
      if (!value[locale]?.trim()) missing[locale] += 1;
    }
  }
  return missing;
}
